import { useState, useEffect } from 'react'
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Field,
  Input,
  Textarea,
  Dropdown,
  Option,
} from '@fluentui/react-components'
import { useCaseStore, Case, CaseType, CaseStatus, CasePriority, EditableCaseFields } from '../stores/caseStore'

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
  },
})

interface EditCaseDialogProps {
  open: boolean
  onClose: () => void
  caseItem: Case
}

export function EditCaseDialog({ open, onClose, caseItem }: EditCaseDialogProps) {
  const styles = useStyles()
  const updateCase = useCaseStore((s) => s.updateCase)

  const [name, setName] = useState(caseItem.name)
  const [description, setDescription] = useState(caseItem.description)
  const [caseType, setCaseType] = useState<CaseType>(caseItem.type)
  const [status, setStatus] = useState<CaseStatus>(caseItem.status)
  const [priority, setPriority] = useState<CasePriority>(caseItem.priority)
  const [location, setLocation] = useState(caseItem.location)
  const [incidentDate, setIncidentDate] = useState(caseItem.incidentDate)
  const [assignedTo, setAssignedTo] = useState(caseItem.assignedTo)
  const [notes, setNotes] = useState(caseItem.notes)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setName(caseItem.name)
      setDescription(caseItem.description)
      setCaseType(caseItem.type)
      setStatus(caseItem.status)
      setPriority(caseItem.priority)
      setLocation(caseItem.location)
      setIncidentDate(caseItem.incidentDate)
      setAssignedTo(caseItem.assignedTo)
      setNotes(caseItem.notes)
      setError('')
    }
  }, [open, caseItem])

  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Case name is required')
      return
    }

    updateCase(caseItem.id, {
      name: name.trim(),
      description: description.trim(),
      type: caseType,
      status,
      priority,
      location: location.trim(),
      incidentDate,
      assignedTo: assignedTo.trim(),
      notes: notes.trim(),
    } satisfies EditableCaseFields)

    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit Case</DialogTitle>
          <DialogContent className={styles.content}>
            <Field
              label="Case Name"
              required
              validationMessage={error}
              validationState={error ? 'error' : 'none'}
            >
              <Input
                value={name}
                onChange={(_, data) => {
                  setName(data.value)
                  setError('')
                }}
                placeholder="Enter case name"
              />
            </Field>

            <Field label="Description">
              <Textarea
                value={description}
                onChange={(_, data) => setDescription(data.value)}
                placeholder="Enter case description (optional)"
                rows={3}
              />
            </Field>

            <div className={styles.row}>
              <Field label="Case Type">
                <Dropdown
                  value={caseType}
                  selectedOptions={[caseType]}
                  onOptionSelect={(_, data) => setCaseType(data.optionValue as CaseType)}
                >
                  <Option value="investigation">Investigation</Option>
                  <Option value="surveillance">Surveillance</Option>
                  <Option value="incident">Incident</Option>
                  <Option value="other">Other</Option>
                </Dropdown>
              </Field>

              <Field label="Status">
                <Dropdown
                  value={status}
                  selectedOptions={[status]}
                  onOptionSelect={(_, data) => setStatus(data.optionValue as CaseStatus)}
                >
                  <Option value="active">Active</Option>
                  <Option value="closed">Closed</Option>
                  <Option value="archived">Archived</Option>
                </Dropdown>
              </Field>
            </div>

            <div className={styles.row}>
              <Field label="Priority">
                <Dropdown
                  value={priority}
                  selectedOptions={[priority]}
                  onOptionSelect={(_, data) => setPriority(data.optionValue as CasePriority)}
                >
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="critical">Critical</Option>
                </Dropdown>
              </Field>

              <Field label="Incident Date">
                <Input
                  type="date"
                  value={incidentDate}
                  onChange={(_, data) => setIncidentDate(data.value)}
                />
              </Field>
            </div>

            <Field label="Location">
              <Input
                value={location}
                onChange={(_, data) => setLocation(data.value)}
                placeholder="Enter incident location (optional)"
              />
            </Field>

            <Field label="Assigned To">
              <Input
                value={assignedTo}
                onChange={(_, data) => setAssignedTo(data.value)}
                placeholder="Enter assigned officer/detective (optional)"
              />
            </Field>

            <Field label="Notes">
              <Textarea
                value={notes}
                onChange={(_, data) => setNotes(data.value)}
                placeholder="Enter additional notes (optional)"
                rows={3}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSubmit}>
              Save Changes
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
